// AuthProvider - Sistema de autenticación unificado para Admin y CRM
// location se inyecta desde App (AuthProviderWithRouter) para no usar useLocation() fuera de <Router>.
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { authService } from '@/services/authService';
import { api } from '@/services/api';
import TokenStorage from '@/utils/tokenStorage';
import type { User } from '@/types/auth';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  login: (email: string, password: string) => Promise<void>;
  requestOtp: (identifier: string) => Promise<void>;
  loginOtp: (identifier: string, code: string) => Promise<void>;
  validateHiringCode: (hiringCode: string) => Promise<{
    valid: boolean;
    hiring_code: string;
    client_email?: string;
    client_phone?: string;
    client_name?: string;
    masked_email?: string;
    masked_phone?: string;
  }>;
  loginWithHiringCode: (hiringCode: string, identifier: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
  /** Solo para tests: simula el pathname de la ruta actual sin depender de window.location */
  _testPathname?: string;
}

function getInitialPathname(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '/';
}

export function AuthProvider({ children, _testPathname }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pathname] = useState(() => _testPathname ?? getInitialPathname());

  // Rutas públicas que no requieren autenticación
  const isPublicRoute = useCallback((pathname: string): boolean => {
    const publicRoutes = [
      '/',
      '/clientes',
      '/contratacion/',
      '/hiring/',
      '/expirado',
      '/404',
      '/privacidad',
      '/privacy',
      '/borrador',
      '/colaboradores',
      '/closer',
      '/auth/login',
      '/auth/login-otp',
      '/clientes/activar/',
      // Landings públicas de marketing (tráfico anónimo desde ads).
      // Sin esto, cada visitor dispara un GET /users/me que falla con 401
      // y añade ~300-500 ms de latencia + ruido en logs sin razón.
      '/leads/',
      '/regularizacion',
      // Captación física Madrid (QR locutorios). Tráfico anónimo: sin esto cada
      // escaneo dispararía un GET /users/me 401 con latencia y ruido inútiles.
      '/qr/',
    ];

    // Usar === para rutas exactas cortas ('/' nunca debe hacer startsWith pues todo empieza por '/')
    // Usar startsWith solo para rutas prefix con longitud > 1 (ej: '/hiring/', '/contratacion/')
    return publicRoutes.some(route =>
      pathname === route ||
      (route.length > 1 && pathname.startsWith(route))
    );
  }, []);

  const checkAuth = useCallback(async () => {
    setIsLoading(true);

    const token = authService.getAccessToken();

    // Si no hay token en localStorage/cookies/sessionStorage, intentar igualmente
    // vía httpOnly cookies (cross-subdomain con domain=.migro.es).
    // El backend autentica desde la cookie access_token httpOnly si existe.
    if (!token && isPublicRoute(pathname)) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    try {
      // Validar token contra el backend (usa Bearer header O httpOnly cookie)
      const response = await api.get('/users/me');
      const userData = response.data;
      
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
        console.log('🔍 [AuthProvider] Datos del backend /users/me:', { email: userData.email, role: userData.role });
      }
      
      // Mapear a tipo User
      const mappedUser: User = {
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name,
        first_name: userData.first_name,
        last_name: userData.last_name,
        phone_number: userData.phone_number,
        avatar_url: userData.avatar_url,
        photo_avatar_url: userData.photo_avatar_url,
        bio: userData.bio,
        is_active: userData.is_active,
        is_verified: userData.is_verified,
        is_superuser: userData.is_superuser || userData.role === 'admin' || userData.role === 'superuser',
        role: userData.role,
        fcm_registered: userData.fcm_registered || false,
        last_login: userData.last_login,
        email_verified_at: userData.email_verified_at,
        created_at: userData.created_at,
        updated_at: userData.updated_at,
      };
      
      if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
        console.log('✅ [AuthProvider] Usuario mapeado:', { email: mappedUser.email, role: mappedUser.role });
      }
      
      
      setUser(mappedUser);
      
      // Calcular is_admin para localStorage
      const is_admin = mappedUser.is_superuser || mappedUser.role === 'admin' || mappedUser.role === 'superuser';
      
      
      // Guardar también en formato admin_user para compatibilidad
      try {
        localStorage.setItem('admin_user', JSON.stringify({
          id: mappedUser.id,
          email: mappedUser.email,
          name: mappedUser.full_name || mappedUser.email,
          is_admin: is_admin,
          is_superuser: mappedUser.is_superuser,
          role: mappedUser.role,
        }));
      } catch {
        // localStorage puede no estar disponible en navegación privada
      }

    } catch (error: unknown) {
      console.error('Error verificando autenticación:', error);
      const err = error as { response?: { status?: number } };

      // Solo limpiar tokens si es un error de autenticación (401/403) y no hay refresh token disponible
      // Si hay refresh token, el interceptor de axios debería manejarlo
      // NO limpiar en errores temporales (500, 404, timeout, etc.)
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        const refreshToken = TokenStorage.getRefreshToken();
        const hasLocalTokens = TokenStorage.hasTokens();
        if (!hasLocalTokens) {
          // No hay tokens en localStorage — probablemente intentó via httpOnly cookie y falló
          // Solo setear user null, sin clearAuth (no hay nada que limpiar)
          setUser(null);
        } else if (!refreshToken || TokenStorage.isRefreshTokenExpired()) {
          // Solo limpiar si realmente no hay forma de refrescar
          clearAuth();
        } else {
          // Hay refresh token disponible, dejar que el interceptor lo maneje
          if (import.meta.env.DEV) {
            console.log('⚠️ Error 401/403 pero hay refresh token disponible, esperando refresh automático');
          }
        }
      } else {
        // Para otros errores (500, 404, timeout, etc.), mantener la sesión
        // Los tokens NO se descartan en errores temporales
        // Si hay tokens válidos, mantener el estado anterior del usuario si existe
        let existingUser: string | null = null;
        try { existingUser = localStorage.getItem('admin_user'); } catch { /* privado */ }
        if (existingUser && TokenStorage.hasTokens()) {
          try {
            const parsedUser = JSON.parse(existingUser);
            // Restaurar usuario desde localStorage si hay tokens válidos
            // Esto evita que se pierda la sesión por errores temporales
            setUser({
              id: parsedUser.id,
              email: parsedUser.email,
              full_name: parsedUser.name || parsedUser.email,
              first_name: parsedUser.name?.split(' ')[0] || '',
              last_name: parsedUser.name?.split(' ').slice(1).join(' ') || '',
              phone_number: '',
              avatar_url: '',
              photo_avatar_url: '',
              bio: '',
              is_active: true,
              is_verified: true,
              is_superuser: parsedUser.is_superuser || false,
              role: parsedUser.role || 'user',
              fcm_registered: false,
              last_login: null,
              email_verified_at: null,
              created_at: '',
              updated_at: '',
            });
            if (import.meta.env.DEV) {
              console.log('✅ Restaurado usuario desde localStorage después de error temporal');
            }
          } catch (e) {
            console.error('Error restaurando usuario desde localStorage:', e);
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Inicializar base de datos local al montar
  useEffect(() => {
    // Inicializar base de datos SQLite en segundo plano
    import('@/services/localDatabase').then(({ localDatabase }) => {
      localDatabase.initialize().catch((error) => {
        console.error('Error inicializando base de datos local:', error);
      });
    });
  }, []);

  // Verificar autenticación al montar
  useEffect(() => {
    // No verificar autenticación en rutas públicas
    if (isPublicRoute(pathname)) {
      setIsLoading(false);
      return;
    }

    // Siempre intentar checkAuth en rutas protegidas:
    // - Si hay token en localStorage → lo envía como Bearer header
    // - Si no hay token en localStorage → la httpOnly cookie (domain=.migro.es) se envía automáticamente
    // Esto permite restaurar sesión cross-subdomain (crm.migro.es ↔ api.migro.es)
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar al montar - verificar una sola vez
  
  // Sincronizar logout entre tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_user' && e.newValue === null && user) {
        // Otra tab hizo logout, limpiar estado local
        setUser(null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user]);

  // Verificar autenticación cuando cambia la ruta si no hay usuario cargado
  useEffect(() => {
    // Si no hay usuario y no está cargando, intentar cargar (httpOnly cookies pueden estar presentes)
    if (!user && !isLoading && !isPublicRoute(pathname)) {
      checkAuth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const loadUserInProgressRef = useRef(false);

  const clearAuth = () => {
    TokenStorage.clearTokens();
    try { localStorage.removeItem('admin_user'); } catch { /* privado */ }
    setUser(null);
  };

  const loadAndSetUser = useCallback(async () => {
    if (loadUserInProgressRef.current) return;
    loadUserInProgressRef.current = true;
    try {
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
      console.log('🔍 [AuthProvider] loadAndSetUser - Iniciando carga de usuario...');
    }
    
    const response = await api.get('/users/me');
    const userData = response.data;

    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
      console.log('🔍 [AuthProvider] loadAndSetUser - Datos del backend /users/me:', {
        email: userData.email,
        role: userData.role,
        is_superuser: userData.is_superuser,
      });
    }

    const mappedUser: User = {
      id: userData.id,
      email: userData.email,
      full_name: userData.full_name,
      first_name: userData.first_name,
      last_name: userData.last_name,
      phone_number: userData.phone_number,
      avatar_url: userData.avatar_url,
      photo_avatar_url: userData.photo_avatar_url,
      bio: userData.bio,
      is_active: userData.is_active,
      is_verified: userData.is_verified,
      is_superuser: userData.is_superuser || userData.role === 'admin' || userData.role === 'superuser',
      role: userData.role,
      fcm_registered: userData.fcm_registered || false,
      last_login: userData.last_login,
      email_verified_at: userData.email_verified_at,
      created_at: userData.created_at,
      updated_at: userData.updated_at,
    };

    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG === 'true') {
      console.log('✅ [AuthProvider] loadAndSetUser - Usuario mapeado:', { email: mappedUser.email, role: mappedUser.role });
    }

    setUser(mappedUser);

    const is_admin = mappedUser.is_superuser || mappedUser.role === 'admin' || mappedUser.role === 'superuser';
    
    try {
      localStorage.setItem('admin_user', JSON.stringify({
        id: mappedUser.id,
        email: mappedUser.email,
        name: mappedUser.full_name || mappedUser.email,
        is_admin: is_admin,
        is_superuser: mappedUser.is_superuser,
        role: mappedUser.role,
      }));
    } catch { /* privado */ }
    } finally {
      loadUserInProgressRef.current = false;
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await authService.login(email, password);

      await loadAndSetUser();
      
    } catch (error: unknown) {
      clearAuth();
      throw error;
    }
  };

  const requestOtp = async (identifier: string) => {
    // No modifica sesión: solo dispara el envío del código.
    await authService.requestOtp(identifier);
  };

  const loginOtp = async (identifier: string, code: string) => {
    try {
      await authService.verifyOtp(identifier, code);
      await loadAndSetUser();
    } catch (error: unknown) {
      clearAuth();
      throw error;
    }
  };

  const validateHiringCode = async (hiringCode: string) => {
    // No modifica sesión: solo valida el código y retorna info
    return await authService.validateHiringCode(hiringCode);
  };

  const loginWithHiringCode = async (hiringCode: string, identifier: string) => {
    try {
      await authService.loginWithHiringCode(hiringCode, identifier);
      await loadAndSetUser();
    } catch (error: unknown) {
      clearAuth();
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Error en logout:', error);
    } finally {
      clearAuth();
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  const isAdmin = user ? (user.is_superuser || user.role === 'admin' || user.role === 'superuser') : false;

  // Considerar autenticado si:
  // 1. Hay usuario cargado y activo (caso normal)
  // 2. Está cargando (evita redirecciones prematuras — puede haber httpOnly cookies cross-subdomain)
  const hasValidTokens = TokenStorage.hasTokens() &&
                        TokenStorage.getAccessToken() &&
                        !TokenStorage.isRefreshTokenExpired();
  const isAuthenticated: boolean = Boolean((!!user && user.is_active) || (isLoading && (hasValidTokens || !isPublicRoute(pathname))));

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        isAdmin,
        login,
        requestOtp,
        loginOtp,
        validateHiringCode,
        loginWithHiringCode,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Ya no necesario: el pathname se inyecta en AuthProvider desde App (AuthProviderWithRouter).
 * Se mantiene por compatibilidad; solo renderiza children.
 */
export function AuthPathnameSync({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
